import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const logout = useCallback(() => {
    const isDemoUser = user?.email === 'demo@example.com';
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    
    // For demo users, ensure we're on a public route
    if (isDemoUser) {
      const currentPath = window.location.pathname;
      const publicRoutes = ['/', '/about', '/pricing', '/contact', '/privacy', '/cookies', '/termsofservice', 
                            '/login', '/signup', '/analytics-features', '/content-features', 
                            '/compliance-features', '/revenue-features', '/metrics-dictionary', '/guides'];
      const isPublicRoute = publicRoutes.includes(currentPath) || currentPath.startsWith('/guides/');
      
      if (!isPublicRoute) {
        // Redirect to homepage to ensure clean state
        window.location.href = '/';
      }
    }
  }, [user]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get('/api/auth/profile');
      if (response.data.user?.status === 'pending') {
        logout();
        setLoading(false);
        window.location.href = '/waitlist';
        return;
      }
      setUser(response.data.user);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    }
  }, [logout]);
  
  // Track if we've already fetched user to prevent duplicate calls
  const hasFetchedUser = useRef(false);

  // Set up axios interceptor for 401 responses (expired/invalid tokens)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Don't log out if this is an authentication endpoint (login/signup/oauth)
          // These endpoints may return 401 as part of normal auth flow
          const requestUrl = error.config?.url || '';
          const isAuthEndpoint = requestUrl.includes('/api/auth/register') || 
                                 requestUrl.includes('/api/auth/login') ||
                                 requestUrl.includes('/api/auth/google') ||
                                 requestUrl.includes('/api/auth?path=/google-oauth');
          
          if (!isAuthEndpoint) {
            // Token expired or invalid - clear auth and redirect to login
            console.log('Token expired or invalid, logging out...');
            logout();
            // Only redirect if we're not already on login/signup pages
            if (window.location.pathname !== '/login' && 
                window.location.pathname !== '/signup' &&
                !window.location.pathname.includes('/auth/google-oauth-callback')) {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  // Clear demo session when navigating to public routes
  useEffect(() => {
    if (!user || user.email !== 'demo@example.com') return;

    const currentPath = window.location.pathname;
    const publicRoutes = ['/', '/about', '/pricing', '/contact', '/privacy', '/cookies', '/termsofservice', 
                          '/login', '/signup', '/analytics-features', '/content-features', 
                          '/compliance-features', '/revenue-features', '/metrics-dictionary', '/guides'];
    const isPublicRoute = publicRoutes.includes(currentPath) || currentPath.startsWith('/guides/');
    
    // If demo user navigates to public routes, clear session
    if (isPublicRoute) {
      console.log('Demo user navigating to public route, clearing session...');
      logout();
    }
  }, [user, logout]);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Only fetch user if we don't have one yet and haven't fetched before
      if (!user && !hasFetchedUser.current) {
        hasFetchedUser.current = true;
        fetchUser();
      }
    } else {
      hasFetchedUser.current = false;
      setLoading(false);
    }
  }, [token, user, fetchUser]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

